import React, { useEffect, useState } from "react";
import { formatDate } from "../../../core/utils/dateUtils";
import { capitalizeText } from "../../../core/utils/stringUtils";
import apiService from "../../../core/services/apiService";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CalendarModalDetails from "../../../core/components/calendar/CalendarModalDetails";
import TablePagination from "./TableUtils/TablePagination";
import { dateFilters } from "../Dashboard/utils/dateFilters";
import DateFilter from "./TableUtils/DateFilter";
import CommonTableSkeleton from "../../../core/components/skeleton/CommonTableSkeleton";

export default function ALLAppointments({ data }) {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { refreshKey } = useSelector((state) => state.utils);

  const [appointments, setAppointments] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateFilter, setDateFilter] = useState("All");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);

  useEffect(() => {
    const calculateEntries = () => {
      const rowHeight = 37; // approximate table row height (adjust if needed)
      const headerHeight = 300; // includes filters + search area
      const availableHeight = window.innerHeight - headerHeight;

      // Rough estimate: how many rows can fit in remaining space
      const newEntries = Math.max(5, Math.floor(availableHeight / rowHeight));
      setRowsPerPage(newEntries);
    };

    // Run on load and when resizing window
    calculateEntries();
    window.addEventListener("resize", calculateEntries);

    return () => window.removeEventListener("resize", calculateEntries);
  }, []);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const userAppointments = await apiService.get(dispatch, "appointments");
        const sortedAppointments = (userAppointments || []).sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        setAppointments(sortedAppointments || []);
      } catch (error) {
        console.error("Error fetching appointment details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id, dispatch, refreshKey, data]);

  const clearFilters = () => {
    setFilters({});
    setDateFilter("All");
    setCurrentPage(1);
    setSearchTerm("");
    setShowFilters(false);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key)
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      return { key, direction: "asc" };
    });
  };

  const SortArrow = ({ columnKey }) =>
    sortConfig.key !== columnKey ? null : (
      <span className="ml-1 text-xs">
        {sortConfig.direction === "asc" ? "▲" : "▼"}
      </span>
    );

  // 🧩 Define filterConfig (aligned with your TableFilters logic)
  const doctorOptions = [
    ...new Set(appointments.map((a) => a.doctor?.name).filter(Boolean)),
  ];
  const patientOptions = [
    ...new Set(appointments.map((a) => a.patient?.name).filter(Boolean)),
  ];
  const statusOptions = [
    ...new Set(appointments.map((a) => a.status).filter(Boolean)),
  ];

  const filterConfig = [
    {
      key: "time",
      label: "Session",
      type: "select",
      options: [
        { label: "Morning", value: "Morning" },
        { label: "Afternoon", value: "Afternoon" },
      ],
    },
    {
      key: "doctor.name",
      label: "Doctor",
      type: "select",
      options: doctorOptions.map((d) => ({ value: d, label: d })),
    },
    {
      key: "patient.name",
      label: "Patient",
      type: "select",
      options: patientOptions.map((p) => ({ value: p, label: p })),
    },
    { key: "reason", label: "Reason", type: "text" },
    {
      key: "status",
      label: "Status",
      type: "select",
      options: statusOptions.map((s) => ({
        value: s,
        label: capitalizeText(s),
      })),
    },
    { key: "notes", label: "Doctor Notes", type: "text" },
    { key: "date", label: "Date", type: "date" },
  ];

  // 🧠 Utility for nested value access
  const getNestedValue = (obj, path) =>
    path.split(".").reduce((acc, part) => acc && acc[part], obj);

  const parsePHDate = (str, endOfDay = false) => {
    const [y, m, d] = str.split("-").map(Number);
    const date = new Date(y, m - 1, d);

    if (endOfDay) {
      date.setHours(23, 59, 59, 999); // FULL day
    }

    return date;
  };

  // Date filtering
  const applyDateFilter = (data) => {
    if (dateFilter === "All") return data;

    let start = null;
    let end = null;

    if (dateFilter === "Custom Range" && customStart && customEnd) {
      start = parsePHDate(customStart);
      end = parsePHDate(customEnd, true);
    } else if (dateFilters[dateFilter]) {
      [start, end] = dateFilters[dateFilter]();
    }

    return data.filter((t) => {
      const created = new Date(t.date);
      return !start || (created >= start && created <= end);
    });
  };

  const filteredByDate = applyDateFilter(appointments);

  // 🔍 Apply search + filters
  const filteredAppointments = filteredByDate.filter((app) => {
    const matchesSearch =
      !searchTerm ||
      app.doctor?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.patient?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.notes?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilters = Object.entries(filters).every(([key, val]) => {
      if (!val) return true;

      const recordVal = getNestedValue(app, key);
      const filterValue =
        typeof val === "object" && val.value ? val.value : val;

      // Date = exact match
      if (key.toLowerCase().includes("date")) {
        return formatDate(recordVal) === formatDate(filterValue);
      }

      // Text match (partial, case-insensitive)
      return recordVal
        ?.toString()
        .toLowerCase()
        .includes(filterValue.toString().toLowerCase());
    });

    return matchesSearch && matchesFilters;
  });

  // Helper to get nested values like doctor.name
  const getValue = (obj, path) =>
    path.split(".").reduce((o, key) => (o ? o[key] : null), obj);

  // Sorting
  const sortedData = [...filteredAppointments].sort((a, b) => {
    const { key, direction } = sortConfig;

    let valA = getValue(a, key);
    let valB = getValue(b, key);

    // Convert dates
    if (key === "date" || key === "created_on") {
      valA = new Date(valA);
      valB = new Date(valB);
    }

    // Normalize strings
    if (typeof valA === "string") valA = valA.toLowerCase();
    if (typeof valB === "string") valB = valB.toLowerCase();

    if (valA < valB) return direction === "asc" ? -1 : 1;
    if (valA > valB) return direction === "asc" ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(filteredAppointments.length / rowsPerPage);
  const paginatedAppointments = sortedData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  if (!loading && !appointments.length)
    return (
      <div className="text-center text-gray-500 p-6">
        No consultation history found.
      </div>
    );

  return (
    <div className="bg-white rounded-lg shadow border p-4">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="px-3 py-1.5 border rounded-lg text-sm w-56 focus:outline-none focus:ring focus:ring-blue-200"
        />

        {/* ✅ Extracted Date Filter */}
        <DateFilter
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
          customStart={customStart}
          setCustomStart={setCustomStart}
          customEnd={customEnd}
          setCustomEnd={setCustomEnd}
          onFilterChange={() => setCurrentPage(1)}
        />
      </div>

      {/* 📋 Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm table-fixed">
          <thead>
            <tr className="bg-gray-100 text-left select-none">
              <th
                className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                onClick={() => handleSort("date")}
              >
                Date <SortArrow columnKey="date" />
              </th>

              <th
                className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                onClick={() => handleSort("time")}
              >
                Session <SortArrow columnKey="time" />
              </th>

              <th
                className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                onClick={() => handleSort("doctor.name")}
              >
                Doctor <SortArrow columnKey="doctor.name" />
              </th>

              <th
                className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                onClick={() => handleSort("patient.name")}
              >
                Patient <SortArrow columnKey="patient.name" />
              </th>

              <th
                className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                onClick={() => handleSort("reason")}
              >
                Reason <SortArrow columnKey="reason" />
              </th>

              <th
                className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                onClick={() => handleSort("status")}
              >
                Status <SortArrow columnKey="status" />
              </th>

              <th
                className="px-4 py-2 border-b font-medium text-gray-600 cursor-pointer"
                onClick={() => handleSort("notes")}
              >
                Notes <SortArrow columnKey="notes" />
              </th>
            </tr>
          </thead>
          {loading ? (
            <CommonTableSkeleton columns={filterConfig} rows={rowsPerPage} />
          ) : (
            <tbody className="divide-y divide-gray-200">
              {paginatedAppointments.map((app) => (
                <tr
                  key={app._id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedReport(app)}
                >
                  <td className="px-4 py-2">{formatDate(app.date)}</td>
                  <td className="px-4 py-2">{app.time}</td>
                  <td className="px-4 py-2">{app.doctor?.name || "N/A"}</td>
                  <td className="px-4 py-2">{app.patient?.name || "N/A"}</td>
                  <td className="px-4 py-2 max-w-[200px] truncate">
                    {app.reason || "-"}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                        app.status === "completed"
                          ? "bg-green-100 text-green-700"
                          : app.status === "cancelled"
                          ? "bg-red-100 text-red-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {capitalizeText(app.status)}
                    </span>
                  </td>
                  <td className="px-4 py-2 max-w-[250px] truncate">
                    {app.notes || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      {/* 📑 Pagination + Record Count */}
      <TablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={filteredAppointments.length}
        rowsPerPage={rowsPerPage}
        onPageChange={setCurrentPage}
      />

      {/* Modal */}
      {selectedReport && (
        <CalendarModalDetails
          report={selectedReport}
          onClose={() => setSelectedReport(null)}
          onRefresh={() => setSelectedReport(null)}
        />
      )}
    </div>
  );
}
